import { Getter } from '../Getter';
import { HttpError } from '../HttpError';
import { ApiTopFiveMetrics } from './Models/ApiTopFiveMetrics';
import { TopFiveMetrics } from './Models/TopFiveMetrics';
import isValidUUID from 'uuid-validate';
import { getMetricFromEntry } from './utils/getMetricFromEntry';
import { logEvent, DASHBOARD_INVALID_METRIC_SEEN } from 'utils/analytics';

type ApiResponse = ApiTopFiveMetrics | null | undefined;

interface DataResponse {
  data?: TopFiveMetrics;
  error?: never;
}
interface ErrorResponse {
  data?: never;
  error: HttpError;
}
export type ClientOutput = DataResponse | ErrorResponse;

export class TopFiveMetricsClient {
  private getter: Getter<ApiResponse | HttpError>;

  constructor(getter: Getter<ApiResponse | HttpError>) {
    this.getter = getter;
  }

  public async retrieveAsync(organizationUUID: string): Promise<ClientOutput> {
    if (!isValidUUID(organizationUUID)) {
      // equivalent to the response we'd get on the server
      return {
        error: new HttpError(400),
      };
    }

    const response = await this.tryRetrieveAsync(organizationUUID);

    if (!response) {
      return {
        data: undefined,
        error: undefined,
      };
    }

    if (response instanceof HttpError) {
      return {
        error: response,
      };
    }

    if (response.version !== 2) {
      throw new Error(
        `Only version 2 of the '${this.getHref(
          organizationUUID
        )}' payload body is supported`
      );
    }

    return {
      data: this.toTopFiveMetrics(response),
    };
  }

  private async tryRetrieveAsync(
    organizationUUID: string
  ): Promise<ApiResponse | HttpError> {
    const href = this.getHref(organizationUUID);

    return await this.getter.get(href);
  }

  private toTopFiveMetrics(response: ApiTopFiveMetrics): TopFiveMetrics {
    const { period, time, entries } = response;

    const metrics = entries.map(getMetricFromEntry);
    const knownMetrics = metrics.filter((metric) => metric.type !== undefined);

    if (metrics.length !== knownMetrics.length) {
      logEvent(DASHBOARD_INVALID_METRIC_SEEN);
    }

    return {
      period: {
        type: period.type,
        from: new Date(period.from),
        value: period.value,
      },
      insightCreatedDate: new Date(time),
      metrics: knownMetrics,
    };
  }

  private getHref(organizationUUID: string): string {
    return `/api/v1/organizations/${organizationUUID}/benchmark-insight/top_five_metrics`;
  }
}
